/**
 * The Stock transfers ranking and filters (`docs/adr/9034`), in a module of their own so the
 * Transfer Why list shares them without importing the grid module: a page that reads its ranking
 * does not thereby mount a grid (the surfaces census follows imports).
 */

import type { RankedCapConfig, RankFilter } from '@/planning';

import type { TransferRow } from './api';
import { SUPERSEDED } from './superseded';
import { dispositionStateKey } from './transfer-dispositions';
import { purposeLabelKey } from './transfer-action-types';

/**
 * The decision column's ORDER, which is not the alphabet of its translated words: pending first,
 * then what was approved (as is, or adjusted), deferred, rejected, and the lines a newer run took
 * over. A code this build does not know sorts after all of them. CONFIRMED by the designer (brief
 * part 3 §C3): «da decidere → approvata → modificata → rinviata → rifiutata → sostituita» — what
 * asks for an action first, then the outcomes from the most to the least final.
 */
export const DECISION_ORDER: readonly (string | null)[] = [null, 'approve', 'adjust', 'defer', 'reject', SUPERSEDED];

/** A disposition's place in `DECISION_ORDER`. Pure. */
export function decisionRank(disposition: string | null): number {
  const at = DECISION_ORDER.indexOf(disposition);
  return at === -1 ? DECISION_ORDER.length : at;
}

/** The ranking by status (brief part 3 §C3): its grids open each status with a group row. */
export const STATUS_RANK = 'status';

/**
 * The transfer page's ranking and filters (owner ruling 2026-10-01, `docs/adr/9034`): each action
 * type's grid draws the top fifty of the planner's ranking — net benefit by default, or lane cost,
 * run damage, pairs or status — after the purpose, decision, donor, recipient, article and
 * department-family filters, which every type's grid shares (the last two: usability audit C4,
 * 2026-10-02 — `list_transfers` takes `article_key` and `department_family`). The family is the
 * platform's to resolve: the caller passes the filter `familyFilter` builds over its narrowed read.
 * Each option ranks on a grid column, so the grid's band names the ranking and a header click on
 * that column turns it round.
 */
export function transferRanking(
  t: (key: string) => string,
  family?: RankFilter<TransferRow>,
): RankedCapConfig<TransferRow> {
  return {
    rankOptions: [
      { id: 'net_benefit', labelKey: 'inseason.column.netBenefit', value: (row) => row.netBenefit, direction: 'desc' },
      { id: 'lane_cost', labelKey: 'inseason.column.laneCost', value: (row) => row.laneCost, direction: 'desc' },
      { id: 'run_damage', labelKey: 'inseason.column.runDamage', value: (row) => row.runDamage, direction: 'desc' },
      { id: 'units', labelKey: 'inseason.column.units', value: (row) => row.units, direction: 'desc' },
      // By status, in the confirmed order; within a status, net benefit largest first — whichever
      // way the planner turns the status order (brief part 3 §C3, ADR 9038).
      {
        id: STATUS_RANK, labelKey: 'inseason.transferDecision.column.disposition', value: (row) => decisionRank(row.disposition),
        direction: 'asc', tieBreak: { value: (row) => row.netBenefit, direction: 'desc' },
      },
    ],
    filters: [
      {
        id: 'purpose', labelKey: 'inseason.column.purpose', kind: 'select', value: (row) => row.purpose,
        label: (code) => { const key = purposeLabelKey(code); return key === undefined ? code : t(key); },
      },
      {
        id: 'decision', labelKey: 'inseason.transferDecision.column.disposition', kind: 'select',
        value: (row) => row.disposition ?? PENDING,
        label: (code) => t(dispositionStateKey(code === PENDING ? null : code)),
      },
      {
        id: NET_FILTER, labelKey: 'inseason.column.netBenefit', kind: 'select', value: (row) => netSign(row.netBenefit),
        label: (code) => { const key = NET_LABEL_KEY[code]; return key === undefined ? code : t(key); },
      },
      { id: 'donor', labelKey: 'inseason.transferGrid.filter.donor', kind: 'multi', value: (row) => row.donorStoreKey },
      { id: 'recipient', labelKey: 'inseason.transferGrid.filter.recipient', kind: 'multi', value: (row) => row.recipientStoreKey },
      { id: 'article', labelKey: 'inseason.queueFilter.article', kind: 'multi', value: (row) => row.articleKey },
      ...(family === undefined ? [] : [family]),
    ],
  };
}

/** A pending transfer's value in the decision filter: no disposition is recorded on it. */
export const PENDING = 'pending';

/**
 * The sign of a transfer's net benefit (the `net` filter, ADR 9069): the Stock transfers figures count the moves that pay for their
 * lane (net benefit above nothing) and the ones that would cost more than they repair (below it). A move at exactly nothing is in
 * neither count, so it is a value of its own and never read as a loss. The platform lists the same two sets with `net_benefit`.
 */
export const NET_FILTER = 'net';
export const NET_PAYS = 'pays';
export const NET_COSTS = 'costs';
export const NET_EVEN = 'even';

/** Which side of nothing a transfer's net benefit is on. Pure. */
export function netSign(netBenefit: number): typeof NET_PAYS | typeof NET_COSTS | typeof NET_EVEN {
  return netBenefit > 0 ? NET_PAYS : netBenefit < 0 ? NET_COSTS : NET_EVEN;
}

const NET_LABEL_KEY: Readonly<Record<string, string>> = {
  pays: 'inseason.transferGrid.net.pays',
  costs: 'inseason.transferGrid.net.costs',
  even: 'inseason.transferGrid.net.even',
};
